const YMQ_STICKY_CONFIG = {
  tagName: 'ymq-current-price-sticky',
  styleId: 'ymq-current-price-sticky-style',
  startedAttr: 'data-ymq-current-price-booted',
  topOffsetVar: '--ymq-current-price-sticky-top',
  classes: {
    base: 'ymq-current-price-sticky-bar',
    label: 'ymq-current-price-label',
    value: 'ymq-current-price-value',
    hidden: 'is-hidden',
    visible: 'is-visible'
  },
  timings: {
    interactionDelayMs: 120,
    discoveryTimeoutMs: 12000
  },
  selectors: {
    infoContainer: '.product__info-container',
    insertionTargets: ['ymq-option', '.ymq-options-box'],
    stickyHeader: '.section-header',
    priceContainer: '.product__price',
    priceCandidates: [
      '.product__price .price-item--sale .ymq-price-span',
      '.product__price .price-item--regular .ymq-price-span',
      '.product__price .ymq-price-span'
    ]
  }
};

class YmqCurrentPriceSticky extends HTMLElement {
  constructor() {
    super();
    this.priceObserver = null;
    this.priceContainerObserver = null;
    this.priceVisibilityObserver = null;
    this.interactionEventsBound = false;
    this.legacyVisibilityHandler = null;
    this.handleInteraction = this.handleInteraction.bind(this);
    this.handleWindowResize = this.handleWindowResize.bind(this);
    this.handleWindowLoad = this.handleWindowLoad.bind(this);
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.render();
    this.sync();
    window.addEventListener('resize', this.handleWindowResize);
    window.addEventListener('load', this.handleWindowLoad);
  }

  disconnectedCallback() {
    this.cleanup();
  }

  static init() {
    if (!document.body || document.body.getAttribute(YMQ_STICKY_CONFIG.startedAttr) === 'true') {
      return;
    }

    document.body.setAttribute(YMQ_STICKY_CONFIG.startedAttr, 'true');
    YmqCurrentPriceSticky.ensureStyles();

    if (YmqCurrentPriceSticky.mount()) {
      return;
    }

    const lateMountObserver = new MutationObserver(function () {
      if (YmqCurrentPriceSticky.mount()) {
        lateMountObserver.disconnect();
      }
    });

    lateMountObserver.observe(document.body, { childList: true, subtree: true });
    window.setTimeout(function () {
      lateMountObserver.disconnect();
    }, YMQ_STICKY_CONFIG.timings.discoveryTimeoutMs);
  }

  static ensureStyles() {
    if (document.getElementById(YMQ_STICKY_CONFIG.styleId)) return;

    const style = document.createElement('style');
    style.id = YMQ_STICKY_CONFIG.styleId;
    style.textContent = [
      '.' + YMQ_STICKY_CONFIG.classes.base + ' {',
      '  position: sticky;',
      '  top: var(' + YMQ_STICKY_CONFIG.topOffsetVar + ', 0px);',
      '  z-index: 20;',
      '  width: 100%;',
      '  background: #333;',
      '  color: #fff;',
      '  display: flex;',
      '  align-items: center;',
      '  justify-content: space-between;',
      '  gap: 16px;',
      '  box-sizing: border-box;',
      '  border-radius: 0 0 8px 8px;',
      '  font-size: 18px;',
      '  line-height: 1.2;',
      '  padding-left: 24px;',
      '  padding-right: 24px;',
      '  opacity: 0;',
      '  transform: translateY(-6px);',
      '  visibility: hidden;',
      '  max-height: 0;',
      '  overflow: hidden;',
      '  padding-top: 0;',
      '  padding-bottom: 0;',
      '  pointer-events: none;',
      '  transition: max-height 0.22s ease, padding 0.22s ease, opacity 0.22s ease, transform 0.22s ease;',
      '}',
      '.' + YMQ_STICKY_CONFIG.classes.label + ' { font-weight: 700; }',
      '.' + YMQ_STICKY_CONFIG.classes.value + ' { font-weight: 600; white-space: nowrap; }',
      '.' + YMQ_STICKY_CONFIG.classes.base + '.' + YMQ_STICKY_CONFIG.classes.visible + ' {',
      '  max-height: 120px;',
      '  padding-top: 16px;',
      '  padding-bottom: 16px;',
      '  opacity: 1;',
      '  z-index: 9;',
      '  transform: translateY(0);',
      '  visibility: visible;',
      '  pointer-events: auto;',
      '}',
      '.' + YMQ_STICKY_CONFIG.classes.base + '.' + YMQ_STICKY_CONFIG.classes.hidden + ' {',
      '  max-height: 0;',
      '  padding-top: 0;',
      '  padding-bottom: 0;',
      '  opacity: 0;',
      '  transform: translateY(-6px);',
      '  visibility: hidden;',
      '  pointer-events: none;',
      '}'
    ].join('\n');

    document.head.appendChild(style);
  }

  static query(selector, root) {
    return (root || document).querySelector(selector);
  }

  static getInfoContainer() {
    return YmqCurrentPriceSticky.query(YMQ_STICKY_CONFIG.selectors.infoContainer);
  }

  static getInsertionTarget(infoContainer) {
    if (!infoContainer) return null;

    for (let i = 0; i < YMQ_STICKY_CONFIG.selectors.insertionTargets.length; i += 1) {
      const target = YmqCurrentPriceSticky.query(YMQ_STICKY_CONFIG.selectors.insertionTargets[i], infoContainer);
      if (target) return target;
    }
    return null;
  }

  static mount() {
    const infoContainer = YmqCurrentPriceSticky.getInfoContainer();
    const insertionTarget = YmqCurrentPriceSticky.getInsertionTarget(infoContainer);
    if (!infoContainer || !insertionTarget || !insertionTarget.parentNode) return false;

    let element = document.querySelector(YMQ_STICKY_CONFIG.tagName);
    if (!element) {
      element = document.createElement(YMQ_STICKY_CONFIG.tagName);
    }

    if (insertionTarget.previousElementSibling !== element) {
      insertionTarget.parentNode.insertBefore(element, insertionTarget);
    }
    return true;
  }

  render() {
    this.classList.add(YMQ_STICKY_CONFIG.classes.base, YMQ_STICKY_CONFIG.classes.hidden);
    if (this.querySelector('.' + YMQ_STICKY_CONFIG.classes.label)) return;

    const label = document.createElement('span');
    label.className = YMQ_STICKY_CONFIG.classes.label;
    label.textContent = 'Current Price:';

    const value = document.createElement('span');
    value.className = YMQ_STICKY_CONFIG.classes.value;
    value.textContent = '--';

    this.appendChild(label);
    this.appendChild(value);
  }

  sync() {
    this.updateStickyOffset();
    this.updateDisplayedPrice();
    this.bindInteractionEvents();
    this.setupPriceMutationObservers();
    this.setupPriceVisibilityObserver();
  }

  getCurrentPriceNode() {
    for (let i = 0; i < YMQ_STICKY_CONFIG.selectors.priceCandidates.length; i += 1) {
      const node = YmqCurrentPriceSticky.query(YMQ_STICKY_CONFIG.selectors.priceCandidates[i]);
      if (node) return node;
    }
    return null;
  }

  updateDisplayedPrice() {
    const valueNode = this.querySelector('.' + YMQ_STICKY_CONFIG.classes.value);
    if (!valueNode) return;

    const priceNode = this.getCurrentPriceNode();
    const text = priceNode ? (priceNode.textContent || '').trim() : '';
    if (text) valueNode.textContent = text;
  }

  updateStickyOffset() {
    const stickyHeader = YmqCurrentPriceSticky.query(YMQ_STICKY_CONFIG.selectors.stickyHeader);
    const offset = stickyHeader ? Math.round(stickyHeader.getBoundingClientRect().height) : 0;
    this.style.setProperty(YMQ_STICKY_CONFIG.topOffsetVar, offset + 'px');
  }

  show() {
    this.classList.remove(YMQ_STICKY_CONFIG.classes.hidden);
    this.classList.add(YMQ_STICKY_CONFIG.classes.visible);
  }

  hide() {
    this.classList.remove(YMQ_STICKY_CONFIG.classes.visible);
    this.classList.add(YMQ_STICKY_CONFIG.classes.hidden);
  }

  setupPriceMutationObservers() {
    const activePriceNode = this.getCurrentPriceNode();
    if (!activePriceNode) return;

    this.disconnectObserver('priceObserver');
    this.priceObserver = new MutationObserver(this.updateDisplayedPrice.bind(this));
    this.priceObserver.observe(activePriceNode, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true
    });

    const priceContainer = YmqCurrentPriceSticky.query(YMQ_STICKY_CONFIG.selectors.priceContainer);
    this.disconnectObserver('priceContainerObserver');
    if (!priceContainer) return;

    this.priceContainerObserver = new MutationObserver(
      function () {
        const nextNode = this.getCurrentPriceNode();
        if (!nextNode) return;
        if (nextNode !== activePriceNode) this.setupPriceMutationObservers();
        this.updateDisplayedPrice();
      }.bind(this)
    );
    this.priceContainerObserver.observe(priceContainer, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true
    });
  }

  setupPriceVisibilityObserver() {
    const priceNode = YmqCurrentPriceSticky.query(YMQ_STICKY_CONFIG.selectors.priceContainer);
    if (!priceNode) {
      this.hide();
      return;
    }

    this.disconnectObserver('priceVisibilityObserver');

    if ('IntersectionObserver' in window) {
      this.priceVisibilityObserver = new IntersectionObserver(
        function (entries) {
          const entry = entries[0];
          if (!entry) return;
          if (entry.isIntersecting) this.hide();
          else this.show();
        }.bind(this),
        { threshold: 0.01 }
      );
      this.priceVisibilityObserver.observe(priceNode);
      return;
    }

    this.syncVisibilityFromViewport(priceNode);
    this.bindLegacyVisibilityEvents(priceNode);
  }

  syncVisibilityFromViewport(priceNode) {
    const rect = priceNode.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const isVisible = rect.bottom > 0 && rect.top < viewportHeight;
    if (isVisible) this.hide();
    else this.show();
  }

  bindLegacyVisibilityEvents(priceNode) {
    if (this.legacyVisibilityHandler) return;

    this.legacyVisibilityHandler = function () {
      this.syncVisibilityFromViewport(priceNode);
    }.bind(this);

    window.addEventListener('scroll', this.legacyVisibilityHandler, { passive: true });
    window.addEventListener('resize', this.legacyVisibilityHandler);
    window.addEventListener('load', this.legacyVisibilityHandler);
  }

  bindInteractionEvents() {
    if (this.interactionEventsBound) return;

    document.addEventListener('change', this.handleInteraction, true);
    document.addEventListener('click', this.handleInteraction, true);
    document.addEventListener('input', this.handleInteraction, true);
    this.interactionEventsBound = true;
  }

  handleInteraction(event) {
    const target = event.target;
    if (!target || !target.closest || !target.closest('ymq-option')) return;

    window.setTimeout(
      function () {
        this.updateDisplayedPrice();
        this.setupPriceMutationObservers();
      }.bind(this),
      YMQ_STICKY_CONFIG.timings.interactionDelayMs
    );
  }

  disconnectObserver(observerName) {
    if (!this[observerName]) return;
    this[observerName].disconnect();
    this[observerName] = null;
  }

  cleanup() {
    this.disconnectObserver('priceObserver');
    this.disconnectObserver('priceContainerObserver');
    this.disconnectObserver('priceVisibilityObserver');

    if (this.legacyVisibilityHandler) {
      window.removeEventListener('scroll', this.legacyVisibilityHandler);
      window.removeEventListener('resize', this.legacyVisibilityHandler);
      window.removeEventListener('load', this.legacyVisibilityHandler);
      this.legacyVisibilityHandler = null;
    }

    if (this.interactionEventsBound) {
      document.removeEventListener('change', this.handleInteraction, true);
      document.removeEventListener('click', this.handleInteraction, true);
      document.removeEventListener('input', this.handleInteraction, true);
      this.interactionEventsBound = false;
    }

    window.removeEventListener('resize', this.handleWindowResize);
    window.removeEventListener('load', this.handleWindowLoad);
  }

  handleWindowResize() {
    this.updateStickyOffset();
  }

  handleWindowLoad() {
    this.updateStickyOffset();
  }
}

if (!customElements.get(YMQ_STICKY_CONFIG.tagName)) {
  customElements.define(YMQ_STICKY_CONFIG.tagName, YmqCurrentPriceSticky);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', YmqCurrentPriceSticky.init);
} else {
  YmqCurrentPriceSticky.init();
}
